import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Container, Grid, Typography } from '@mui/material';
import { APIProvider } from '@vis.gl/react-google-maps';
import { captureExceptionWithAttributes } from '@tracewayapp/react';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { GOOGLE_MAPS_API_KEY } from 'src/config-global';
import { useSettingsContext } from 'src/components/settings';
import { useAuthContext } from 'src/auth/hooks';
import useModelData from 'src/hooks/use-model-data';
import { DisplayPageData } from 'src/components/display-page-data';
import Iconify from 'src/components/iconify';
import PageHeader from 'src/components/page-header';
import PageInfoCarousel from 'src/components/page-info-carousel';
import planFormImage from 'src/assets/images/route-buddy/plan-form.png';
import planStopsImage from 'src/assets/images/route-buddy/plan-stops.png';
import planMapImage from 'src/assets/images/route-buddy/plan-map.png';
import planCreateImage from 'src/assets/images/route-buddy/plan-create.png';
import { EldVehicle } from 'src/models';
import { Constants } from 'src/constants';
import { paths } from 'src/routes/paths';
import { useRouter, useSearchParams } from 'src/routes/hooks';
import {
  EMPTY_STOP_LOCATION,
  LatLngLiteral,
  newTripStop,
  PickTarget,
  RouteBuddyDriverStatus,
  RouteBuddySimulateResponse,
  SavedTrip,
  SimClocks,
  STOP_LABELS,
  StopLocation,
  Trip,
  TripStop,
} from './types';
import { directionsToLegs, useDirections } from './use-directions';
import { toSavedTrip } from './trip-utils';
import PlanForm from './plan-form';
import RouteMap, { RouteMapStop } from './route-map';
import VerdictCards from './verdict-cards';
import TripTimeline from './timeline';
import CreateTripDialog from './create-trip-dialog';

const pageInfo = (
  <PageInfoCarousel
    steps={[
      {
        image: planFormImage,
        title: 'Pick a driver',
        text: "Choose the truck or the driver. You see the driver's hours right away. Green means the driver still has time to drive.",
      },
      {
        image: planStopsImage,
        title: 'Add the stops',
        text: 'Add each pickup and delivery. Type the address and set the appointment time. You can add up to 6 stops.',
      },
      {
        image: planMapImage,
        title: 'Plan the trip',
        text: "Click 'Plan the Trip'. The system checks the driver's hours and shows when the truck arrives at every stop. It also shows where the driver must rest.",
      },
      {
        image: planCreateImage,
        title: 'Create the trip',
        text: "Happy with the plan? Click 'Create Trip'. The trip goes to the Track page and tracking starts by itself.",
      },
    ]}
  />
);

type PageModel = {
  driverStatuses?: RouteBuddyDriverStatus[];
  eldVehicles?: EldVehicle[];
};

const FRESH_CLOCKS: SimClocks = { break: 8, drive: 11, shift: 14, cycle: 70 };

function PageContent() {
  const settings = useSettingsContext();
  const { session } = useAuthContext();
  const timezone = (session?.organization?.companyTimezone as string) || 'America/Chicago';

  const dataLoader = useCallback(
    () => ({
      driverStatuses: axios.get(endpoints.routeBuddy.driverStatus).then((response) => response.data),
      eldVehicles: axios.get(endpoints.eldVehicles.findAll).then((response) => response.data),
    }),
    []
  );
  const { data, error, loading } = useModelData<PageModel>(dataLoader);

  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [driverId, setDriverId] = useState<number | null>(null);
  const [originOverride, setOriginOverride] = useState<StopLocation>(EMPTY_STOP_LOCATION);
  const [tripDepartAt, setTripDepartAt] = useState<string | null>(null);
  const [adjustHours, setAdjustHours] = useState(false);
  const [clockEdits, setClockEdits] = useState<SimClocks>(FRESH_CLOCKS);
  const [stops, setStops] = useState<TripStop[]>([newTripStop('PICKUP')]);
  const [picking, setPicking] = useState<PickTarget>(null);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [simulation, setSimulation] = useState<RouteBuddySimulateResponse | null>(null);
  const [simulating, setSimulating] = useState(false);
  const [pendingAutoRun, setPendingAutoRun] = useState(false);
  const [planAttempted, setPlanAttempted] = useState(false);
  const [editingTrip, setEditingTrip] = useState<Trip | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();
  const tripIdParam = searchParams.get('tripId');

  // the starting point stored with an edited trip, the fallback when the
  // driver's live location is missing or cannot be routed from
  const savedOriginRef = useRef<StopLocation | null>(null);

  const { directionsReady, getRoute } = useDirections();

  const selectedDriver = useMemo(
    () => data?.driverStatuses?.find((driver) => driver.eldDriverId === driverId) || null,
    [data?.driverStatuses, driverId]
  );

  // a fresh driver pick starts from their live clocks and location
  useEffect(() => {
    setOriginOverride(EMPTY_STOP_LOCATION);
    setAdjustHours(false);
    setClockEdits(selectedDriver ? selectedDriver.clocks : FRESH_CLOCKS);
  }, [selectedDriver]);

  const origin: LatLngLiteral | null = useMemo(() => {
    if (originOverride.lat !== null && originOverride.lng !== null) {
      return { lat: originOverride.lat, lng: originOverride.lng };
    }
    if (selectedDriver?.lat !== null && selectedDriver?.lat !== undefined) {
      return { lat: selectedDriver.lat, lng: selectedDriver.lng as number };
    }
    return null;
  }, [selectedDriver, originOverride]);

  // stops the driver still has to make; done stops stay in the editor but leave the plan
  const activeStops = useMemo(() => stops.filter((stop) => !stop.done), [stops]);

  const mapStops: RouteMapStop[] = useMemo(
    () =>
      activeStops
        .map((stop, index): RouteMapStop | null =>
          stop.location.lat !== null && stop.location.lng !== null
            ? {
                key: stop.key,
                position: { lat: stop.location.lat, lng: stop.location.lng },
                glyph: (
                  <Iconify icon={stop.label === 'PICKUP' ? 'glyphs-poly:box-1' : 'glyphs-poly:flag'} width={17} />
                ),
                title: `Stop ${index + 1} · ${STOP_LABELS[stop.label]}`,
              }
            : null
        )
        .filter((stop): stop is RouteMapStop => stop !== null),
    [activeStops]
  );

  const handleMapClick = (point: LatLngLiteral) => {
    const picked: StopLocation = {
      address: `${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}`,
      lat: point.lat,
      lng: point.lng,
    };
    if (picking === 'origin') {
      setOriginOverride(picked);
    } else if (typeof picking === 'number') {
      setStops(stops.map((stop, index) => (index === picking ? { ...stop, location: picked } : stop)));
    }
    setPicking(null);
  };

  const runSimulation = async (directionsResult: google.maps.DirectionsResult) => {
    if (!selectedDriver) {
      return;
    }
    setSimulating(true);
    try {
      const response = await axios.post(endpoints.routeBuddy.simulate, {
        eldDriverId: selectedDriver.eldDriverId,
        departAt: tripDepartAt,
        legs: directionsToLegs(directionsResult),
        stops: activeStops.map((stop) => ({
          label: stop.label,
          appointmentAt: stop.appointmentAt,
          dwellHours: stop.dwellHours,
          departAt: stop.departAt,
        })),
        clockOverrides: adjustHours ? clockEdits : null,
      });
      setSimulation(response.data);
    } catch (requestError: any) {
      const validations = requestError?.response?.data;
      if (Array.isArray(validations)) {
        validations.forEach((validation: { message: string }) =>
          enqueueSnackbar(validation.message, { variant: 'error' })
        );
      } else {
        enqueueSnackbar('Could not plan this trip', { variant: 'error' });
      }
    } finally {
      setSimulating(false);
    }
  };

  const planTrip = async (originPoint: LatLngLiteral) => {
    setSimulating(true);
    setSimulation(null);
    setPlanAttempted(true);
    try {
      const points: LatLngLiteral[] = [
        originPoint,
        ...activeStops.map((stop) => ({ lat: stop.location.lat as number, lng: stop.location.lng as number })),
      ];
      const directionsResult = await getRoute(points);
      if (!directionsResult) {
        throw new Error('no route');
      }
      setDirections(directionsResult);
      await runSimulation(directionsResult);
    } catch (routeError: any) {
      setSimulating(false);

      const saved = savedOriginRef.current;
      if (
        saved?.lat != null &&
        saved?.lng != null &&
        (saved.lat !== originPoint.lat || saved.lng !== originPoint.lng)
      ) {
        savedOriginRef.current = null;
        enqueueSnackbar(
          "Could not route from the driver's live location, planning from the trip's saved starting point instead",
          { variant: 'warning' }
        );
        setOriginOverride(saved);
        await planTrip({ lat: saved.lat, lng: saved.lng });
        return;
      }

      const status = routeError?.code || routeError?.status || '';
      enqueueSnackbar(
        `Could not find a route between these points${status ? ` (${status})` : ''}. Check the driver's location and the stop addresses on the map.`,
        { variant: 'error' }
      );
    }
  };

  const handleRun = async () => {
    // deliberate capture, not a real error: it makes Traceway record the session
    // (screen recording included) so product can watch how Route Buddy is used;
    // the userId/organizationId attributes make the recordings findable
    if (!window.location.href.includes('localhost')) {
      captureExceptionWithAttributes(new Error('Route Buddy: Plan the Trip'), {
        userId: String(session?.user?.id ?? ''),
        organizationId: String(session?.organization?.id ?? ''),
      });
    }

    if (!selectedDriver) {
      enqueueSnackbar('Please pick a driver', { variant: 'error' });
      return;
    }
    if (!origin) {
      enqueueSnackbar("Please set the driver's current location", { variant: 'error' });
      return;
    }
    if (activeStops.length === 0) {
      enqueueSnackbar('All stops are marked done, nothing left to plan', { variant: 'error' });
      return;
    }
    const unresolvedIndex = activeStops.findIndex((stop) => stop.location.lat === null);
    if (unresolvedIndex >= 0) {
      enqueueSnackbar(`Please locate the address for stop ${unresolvedIndex + 1}`, { variant: 'error' });
      return;
    }
    if (!directionsReady) {
      enqueueSnackbar('The map is still loading, try again in a moment', { variant: 'error' });
      return;
    }

    await planTrip(origin);
  };

  // fires when the dispatcher drags the route line into a different path,
  // e.g. to steer around a road the truck cannot take
  const handleRouteEdited = (directionsResult: google.maps.DirectionsResult) => {
    setDirections(directionsResult);
    if (simulation) {
      runSimulation(directionsResult);
    }
  };

  const canSave =
    !!selectedDriver && !!simulation && simulation.stopResults.length === activeStops.length;

  const hydrateFromSavedTrip = (savedTrip: SavedTrip, options?: { forEdit?: boolean; keepDone?: boolean }) => {
    const driver = data?.driverStatuses?.find((d) => d.eldDriverId === savedTrip.eldDriverId);
    if (!driver) {
      enqueueSnackbar(`${savedTrip.driverName} is not an active driver anymore`, { variant: 'error' });
      return false;
    }

    const now = DateTime.now();
    // a started trip keeps its real completions; a not-yet-started trip being
    // edited has no passed stops; a tracked file guesses from the plan
    const stopDone = (stop: SavedTrip['stops'][number]) => {
      if (options?.keepDone) {
        return !!stop.done;
      }
      if (options?.forEdit) {
        return false;
      }
      return DateTime.fromISO(stop.plannedDeparture) < now;
    };
    setVehicleId(null);
    setDriverId(savedTrip.eldDriverId);
    // an edited trip keeps its future departure; a tracked file replans from now
    setTripDepartAt(
      options?.forEdit && savedTrip.tripDepartAt && DateTime.fromISO(savedTrip.tripDepartAt) > now
        ? savedTrip.tripDepartAt
        : null
    );
    setDirections(null);
    setSimulation(null);
    setStops(
      savedTrip.stops.map((stop) => ({
        ...newTripStop(stop.label),
        location: { address: stop.address, lat: stop.lat, lng: stop.lng },
        appointmentAt: stop.appointmentAt,
        dwellHours: stop.dwellHours,
        departAt: stop.departAt,
        plannedArrival: stop.plannedArrival,
        plannedDeparture: stop.plannedDeparture,
        done: stopDone(stop),
      }))
    );
    savedOriginRef.current = savedTrip.origin;
    setPendingAutoRun(true);
    return true;
  };

  // ?tripId= loads a queued or started trip into the planner; a started trip
  // replans the remaining stops from the truck's current position
  useEffect(() => {
    if (!tripIdParam || loading || !data?.driverStatuses) {
      return;
    }
    if (editingTrip?.id === Number(tripIdParam)) {
      return;
    }
    axios
      .get(formatEndpoint(endpoints.trips.findById, { tripId: tripIdParam }))
      .then((response) => {
        const trip: Trip = response.data;
        if (
          trip.status !== Constants.TRIP_STATUS_NEW &&
          trip.status !== Constants.TRIP_STATUS_STARTED
        ) {
          enqueueSnackbar('That trip has already ended, it can no longer be edited', {
            variant: 'warning',
          });
          router.replace(paths.app.routeBuddy.plan);
          return;
        }
        const driver = data?.driverStatuses?.find((d) => d.eldDriverId === trip.eldDriverId);
        const driverName = driver ? `${driver.firstName} ${driver.lastName}` : `Driver ${trip.eldDriverId}`;
        const started = trip.status === Constants.TRIP_STATUS_STARTED;
        if (hydrateFromSavedTrip(toSavedTrip(trip, driverName), { forEdit: true, keepDone: started })) {
          setEditingTrip(trip);
        } else {
          router.replace(paths.app.routeBuddy.plan);
        }
      })
      .catch(() => {
        enqueueSnackbar('Could not load that trip', { variant: 'error' });
        router.replace(paths.app.routeBuddy.plan);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripIdParam, loading, data?.driverStatuses]);

  const handleCreateTrip = async (name: string) => {
    if (!selectedDriver || !simulation || !directions || !canSave) {
      return;
    }
    setCreating(true);
    try {
      const route = directions.routes[0] as any;
      const routePolyline =
        typeof route?.overview_polyline === 'string'
          ? route.overview_polyline
          : route?.overview_polyline?.points ?? '';
      const originLocation =
        originOverride.lat !== null
          ? originOverride
          : { address: 'Live vehicle location', lat: origin?.lat ?? 0, lng: origin?.lng ?? 0 };

      const payload = {
        id: editingTrip?.id ?? 0,
        eldDriverId: selectedDriver.eldDriverId,
        name,
        plannedDepartAt: simulation.segments[0]?.from || new Date().toISOString(),
        originAddress: originLocation.address,
        originLat: originLocation.lat ?? 0,
        originLng: originLocation.lng ?? 0,
        stops: activeStops.map((stop, index) => ({
          label: stop.label,
          address: stop.location.address,
          lat: stop.location.lat as number,
          lng: stop.location.lng as number,
          appointmentAt: stop.appointmentAt,
          dwellHours: stop.dwellHours,
          departAt: stop.departAt,
          plannedArrival: simulation.stopResults[index].arrival,
          plannedDeparture: simulation.stopResults[index].departure,
        })),
        legs: directionsToLegs(directions),
        routePolyline,
        plannedResult: simulation,
      };

      if (editingTrip) {
        await axios.put(endpoints.trips.update, payload);
        enqueueSnackbar('Trip updated', { variant: 'success' });
      } else {
        await axios.post(endpoints.trips.create, payload);
        enqueueSnackbar('Trip created', { variant: 'success' });
      }
      setCreateOpen(false);
      router.push(paths.app.routeBuddy.track);
    } catch (requestError: any) {
      const validations = requestError?.response?.data;
      if (Array.isArray(validations)) {
        validations.forEach((validation: { message: string }) =>
          enqueueSnackbar(validation.message, { variant: 'error' })
        );
      } else {
        enqueueSnackbar('Could not save this trip', { variant: 'error' });
      }
    } finally {
      setCreating(false);
    }
  };

  // after loading a trip, plan from the driver's current position as soon as
  // everything needed is on hand, so the lateness shows without extra clicks
  useEffect(() => {
    if (!pendingAutoRun || !selectedDriver || !directionsReady || loading) {
      return;
    }
    if (!origin) {
      const saved = savedOriginRef.current;
      if (saved?.lat != null && saved?.lng != null) {
        // no live location; plan from the starting point stored in the file and
        // let the effect re-run once the origin lands
        savedOriginRef.current = null;
        setOriginOverride(saved);
        enqueueSnackbar(
          `${selectedDriver.firstName} has no live location, planning from the trip's saved starting point`,
          { variant: 'info' }
        );
        return;
      }
      setPendingAutoRun(false);
      enqueueSnackbar(
        `${selectedDriver.firstName} has no live location, set the starting point and plan the trip`,
        { variant: 'warning' }
      );
      return;
    }
    setPendingAutoRun(false);
    handleRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoRun, selectedDriver, directionsReady, loading, origin]);

  const handleStartFresh = () => {
    setEditingTrip(null);
    setStops([newTripStop('PICKUP')]);
    setDirections(null);
    setSimulation(null);
    setDriverId(null);
    setVehicleId(null);
    setPlanAttempted(false);
    savedOriginRef.current = null;
    if (tripIdParam) {
      router.replace(paths.app.routeBuddy.plan);
    }
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="Route Buddy" info={pageInfo} />

        {editingTrip && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={handleStartFresh}>
            {editingTrip.status === Constants.TRIP_STATUS_STARTED ? (
              <>
                Replanning the started trip{editingTrip.name ? ` "${editingTrip.name}"` : ''}. The
                stops already completed stay as they are; the remaining stops get the new plan and
                tracking continues from it.
              </>
            ) : (
              <>
                Editing the trip{editingTrip.name ? ` "${editingTrip.name}"` : ''} created{' '}
                {DateTime.fromISO(editingTrip.createdAt).toRelative()}. Adjust the plan and save the
                changes, the tracking starts over from the new plan.
              </>
            )}
          </Alert>
        )}

        <PlanForm
          driverStatuses={data?.driverStatuses || []}
          eldVehicles={data?.eldVehicles || []}
          vehicleId={vehicleId}
          setVehicleId={setVehicleId}
          selectedDriver={selectedDriver}
          setDriverId={setDriverId}
          originOverride={originOverride}
          setOriginOverride={setOriginOverride}
          tripDepartAt={tripDepartAt}
          setTripDepartAt={setTripDepartAt}
          adjustHours={adjustHours}
          setAdjustHours={setAdjustHours}
          clockEdits={clockEdits}
          setClockEdits={setClockEdits}
          stops={stops}
          setStops={setStops}
          picking={picking}
          setPicking={setPicking}
          timezone={timezone}
          simulating={simulating}
          onRun={handleRun}
          canSave={canSave}
          editing={!!editingTrip}
          onCreateTrip={() => setCreateOpen(true)}
        />

        {createOpen && selectedDriver && simulation && (
          <CreateTripDialog
            open
            editing={!!editingTrip}
            creating={creating}
            driverName={`${selectedDriver.firstName} ${selectedDriver.lastName}`}
            stopCount={activeStops.length}
            plannedDepartAt={simulation.segments[0]?.from || null}
            timezone={timezone}
            initialName={editingTrip?.name || ''}
            onClose={() => setCreateOpen(false)}
            onConfirm={handleCreateTrip}
          />
        )}

        {/* the map appears once a plan is made or attempted, or earlier when a location is being picked on it */}
        <Grid container spacing={2}>
          {(directions !== null || picking !== null || (planAttempted && origin !== null)) && (
            <Grid item xs={12} md={simulation ? 8 : 12}>
              <Card sx={{ p: 1 }}>
                <RouteMap
                  directions={directions}
                  segments={simulation?.segments || []}
                  driverPosition={origin}
                  stops={mapStops}
                  picking={picking}
                  onMapClick={handleMapClick}
                  onRouteEdited={handleRouteEdited}
                />
                {directions && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, pt: 0.5 }}>
                    Drag the route line to send the driver a different way, the plan recalculates
                    automatically.
                  </Typography>
                )}
                {!directions && picking !== null && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, pt: 0.5 }}>
                    Click the map to set the location.
                  </Typography>
                )}
              </Card>
            </Grid>
          )}
          {simulation && (
            <Grid item xs={12} md={4}>
              <VerdictCards simulation={simulation} stops={activeStops} />
            </Grid>
          )}
        </Grid>

        {simulation && <TripTimeline simulation={simulation} stops={activeStops} />}
      </DisplayPageData>
    </Container>
  );
}

export default function Page() {
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <PageContent />
    </APIProvider>
  );
}
